import { useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import Card from '@mui/material/Card';
import TableRow from '@mui/material/TableRow';
import { Box, Button, Container, IconButton, Switch } from '@mui/material';
import Page from '../../components/Page';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  reset as resetPartner,
  setCurrentPage,
  setLimit,
  setSkip,
  setStatus,
} from '../../store/partner/partnerSlice';
import {
  createPartner,
  findPartner,
  patchPartner,
} from '../../store/partner/api';
import Loader from '../../components/Loader';
import Label from '../../components/Label';
import Iconify from '../../components/Iconify';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import useToggle from '../../hooks/useToggle';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import Scrollbar from '../../components/Scrollbar';
import Searchbar from '../../components/Searchbar';
import PartnerFormModal from './PartnerFormModal';
import { Partner } from '../../store/partner/api/interface';
import { Link } from 'react-router-dom';
import { encryptString } from '../../utils/url';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';

const partnerSchema = Yup.object().shape({
  name: Yup.string().trim().required('Name is required'),
});

const Index: React.FC = () => {
  const dispatch = useAppDispatch();
  const { data, totalRecords, limit, currentPage, status, skip, isLoading } =
    useAppSelector((state) => state.partner);
  const [search, setSearch] = useState<string>('');
  const { toggle, onOpen, onClose } = useToggle();

  useEffect(() => {
    if (status === 'idle') {
      dispatch(
        findPartner({
          limit,
          skip,
          ...(search && { search }),
        })
      );
    }
  }, [dispatch, limit, skip, status]);

  useEffect(() => {
    dispatch(resetPartner());
  }, [search]);

  const {
    handleSubmit,
    values,
    resetForm,
    isSubmitting,
    errors,
    touched,
    handleChange,
    setValues,
  } = useFormik({
    initialValues: {
      id: '',
      name: '',
    },
    validationSchema: partnerSchema,
    onSubmit: async (formValues) => {
      try {
        if (formValues.id) {
          await dispatch(
            patchPartner({ id: formValues.id, name: formValues.name })
          ).unwrap();
        } else {
          await dispatch(createPartner({ name: formValues.name })).unwrap();
        }
        onClose();
        resetForm();
        dispatch(setStatus({ status: 'idle' }));
      } catch (error: any) {}
    },
  });

  const handleUpdate = (value: Partner): void => {
    onOpen();
    setValues({
      id: value.id,
      name: value.name,
    });
  };

  const handleToggleActive = async (value: Partner): Promise<void> => {
    await dispatch(
      patchPartner({ id: value.id, isActive: !value.isActive })
    ).unwrap();
    dispatch(setStatus({ status: 'idle' }));
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
    dispatch(setStatus({ status: 'idle' }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearch(e.target.value);
  };

  return (
    <Page title={'Partners: List'}>
      <Container maxWidth={'xl'}>
        <HeaderBreadcrumbs
          heading={'Partners'}
          links={[{ name: 'Dashboard', href: '/' }]}
          action={
            <Button
              variant='contained'
              startIcon={<Iconify icon={'eva:plus-fill'} />}
              onClick={onOpen}
            >
              New Partner
            </Button>
          }
        />

        <Card>
          <Searchbar handleSearch={handleSearch} />
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800, position: 'relative' }}>
              <Table size={'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell align='center'>Name</TableCell>
                    <TableCell align='center'>Status</TableCell>
                    <TableCell align='center'>Created</TableCell>
                    <TableCell align='center'>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        align='center'
                      >
                        <Loader />
                      </TableCell>
                    </TableRow>
                  ) : data?.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        align='center'
                      >
                        No record found
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.map((partner) => (
                      <TableRow key={partner.id}>
                        <TableCell align='center'>{partner.name}</TableCell>
                        <TableCell align='center'>
                          <Label
                            variant='ghost'
                            color={partner.isActive ? 'success' : 'error'}
                          >
                            {partner.isActive ? 'Active' : 'InActive'}
                          </Label>
                          <Switch
                            checked={partner.isActive}
                            onChange={() => handleToggleActive(partner)}
                          />
                        </TableCell>
                        <TableCell align='center'>
                          {partner.createdAt
                            ? new Date(partner.createdAt).toLocaleString()
                            : '-'}
                        </TableCell>
                        <TableCell align='center'>
                          <IconButton
                            component={Link}
                            to={`/partners/view/${encryptString(partner.id)}`}
                          >
                            <RemoveRedEyeIcon />
                          </IconButton>
                          <IconButton onClick={() => handleUpdate(partner)}>
                            <Iconify icon={'eva:edit-fill'} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>
          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component='div'
              count={totalRecords}
              rowsPerPage={limit}
              page={Math.min(Math.round(totalRecords / limit), currentPage)}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </Box>
        </Card>
        <PartnerFormModal
          touched={touched}
          errors={errors}
          handleChange={handleChange}
          handleSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          onClose={onClose}
          resetForm={resetForm}
          toggle={toggle}
          values={values}
        />
      </Container>
    </Page>
  );
};

export default Index;
